/**
 * AppAiVoice bond setup
 *
 * Registers the dictation engine CATALOG for the Synthase chat mic button —
 * the user picks which engine to use (and download) on first use or via
 * /mic, instead of the app auto-downloading one. Every option runs entirely
 * on-device; the ChatPanel picker renders size/accuracy and disables
 * engines the browser or UI language can't use.
 *
 * wasmPaths: the ONNX runtimes are served same-origin by viteStaticCopy
 * (vite.config.ts) because our script-src 'self' CSP blocks the libs'
 * jsdelivr CDN default. Two dirs — the libs pin different ort versions.
 * Everything heavy (runtimes + models) loads lazily on first dictation use.
 *
 * Moonshine dtype: fp32 everywhere — every QUANTIZED moonshine decoder
 * (q8/int8/q4/q4f16) fails session creation under transformers 4.x's
 * bundled dev ORT (verified live; see the whisper bond's remarks).
 */

import { registerVoiceEngines } from '@molecule/app-ai-voice'
import { provider as webSpeechProvider } from '@molecule/app-ai-voice-default'
import { createProvider as createParakeetProvider } from '@molecule/app-ai-voice-parakeet'
import { createProvider as createWhisperProvider } from '@molecule/app-ai-voice-whisper'

/** Languages the parakeet-tdt-0.6b-v3 ONNX port covers (parakeet.js registry). */
const PARAKEET_LANGUAGES = [
  'en',
  'fr',
  'de',
  'es',
  'it',
  'pt',
  'nl',
  'pl',
  'ru',
  'uk',
  'ja',
  'ko',
  'zh',
] as const

export function setupAppAiVoice(): void {
  registerVoiceEngines([
    {
      id: 'native',
      label: 'Browser dictation',
      kind: 'native',
      accuracy: 2,
      languages: 'all',
      create: () => webSpeechProvider,
    },
    {
      id: 'moonshine',
      label: 'Moonshine',
      kind: 'on-device',
      downloadMB: 247,
      accuracy: 2,
      languages: ['en'],
      create: () =>
        createWhisperProvider({
          model: 'onnx-community/moonshine-base-ONNX',
          dtype: { encoder_model: 'fp32', decoder_model_merged: 'fp32' },
          wasmPaths: '/transformers-ort/',
        }),
    },
    {
      id: 'parakeet',
      label: 'Parakeet (NVIDIA)',
      kind: 'on-device',
      downloadMB: [650, 1240],
      accuracy: 3,
      languages: PARAKEET_LANGUAGES,
      create: () => createParakeetProvider({ wasmPaths: '/ort/' }),
    },
    {
      id: 'whisper',
      label: 'Whisper',
      kind: 'on-device',
      downloadMB: 291,
      accuracy: 1,
      languages: 'all',
      // fp32 like moonshine — quantized decoders fail under transformers 4.x's
      // bundled dev ORT (verified live for whisper-base q8 too).
      create: () =>
        createWhisperProvider({
          dtype: { encoder_model: 'fp32', decoder_model_merged: 'fp32' },
          wasmPaths: '/transformers-ort/',
        }),
    },
  ])
}
